import { type FC, useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import {
  useCurrentPlatformQuery,
  useUpdateCurrentPlatformMutation,
} from "@/partner/entities/platforms";
import type { LiveReactionEmojiType } from "@/types/graphql/partner.generated";
import {
  MAX_PHRASE_LENGTH,
  MAX_PHRASES,
  normalizePhrases,
  PHRASE_ERROR_KEYS,
  phraseError,
} from "../model/phrases";
import {
  ALL_REACTION_EMOJIS,
  MAX_REACTION_EMOJIS,
  REACTION_EMOJI_GLYPHS,
  toggleReactionEmoji,
} from "../model/emojis";

/**
 * Реакции на эфирах (спека live-reactions-and-polls, «Кабинет»): тумблер
 * партнёра, набор эмодзи панели и фразы-облачка. Блок страницы настроек
 * платформы (PlatformSettings.tsx) при фичефлаге LIVE_REACTIONS; сохраняет
 * своими полями через updateCurrentPlatform.
 */
export const LiveReactionsSettingsCard: FC = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { data } = useCurrentPlatformQuery();
  const platform = data?.currentPlatform;
  // Ленивые инициализаторы: при платформе в кеше Apollo первый рендер сразу
  // показывает сохранённые значения и «Сохранить» не мигает активной.
  const [enabled, setEnabled] = useState(
    () => platform?.isLiveReactionsEnabled ?? false
  );
  const [phrases, setPhrases] = useState<string[]>(
    () => platform?.liveReactionPhrases ?? []
  );
  const [emojis, setEmojis] = useState<LiveReactionEmojiType[]>(
    () => platform?.liveReactionEmojis ?? []
  );

  useEffect(() => {
    if (!platform) return;
    setEnabled(platform.isLiveReactionsEnabled);
    setPhrases(platform.liveReactionPhrases);
    setEmojis(platform.liveReactionEmojis);
  }, [platform]);

  const [updateCurrentPlatform, { loading }] = useUpdateCurrentPlatformMutation(
    {
      refetchQueries: ["CurrentPlatform"],
      onCompleted: () =>
        enqueueSnackbar(t("liveReactionsSettings.saved"), {
          variant: "success",
        }),
      onError: (error) => enqueueSnackbar(error.message, { variant: "error" }),
    }
  );

  const errors = phrases.map(phraseError);
  const hasErrors = errors.some((error) => error !== null);
  // Как у формы настроек выше: «Сохранить» активна только при изменениях.
  const isDirty =
    !!platform &&
    (enabled !== platform.isLiveReactionsEnabled ||
      phrases.join("\n") !== platform.liveReactionPhrases.join("\n") ||
      emojis.join(",") !== platform.liveReactionEmojis.join(","));

  const handleSave = () => {
    if (hasErrors) return;
    void updateCurrentPlatform({
      variables: {
        input: {
          isLiveReactionsEnabled: enabled,
          liveReactionPhrases: normalizePhrases(phrases),
          liveReactionEmojis: emojis,
        },
      },
    });
  };

  return (
    <Card sx={{ mt: 2 }}>
      <CardHeader
        title={t("liveReactionsSettings.title")}
        subheader={t("liveReactionsSettings.subtitle")}
        sx={{ px: 2, pt: 2, pb: 0 }}
      />
      <CardContent sx={{ px: 2, pb: 2, pt: 2 }}>
        <Stack spacing={2}>
          <FormControlLabel
            control={
              <Switch
                checked={enabled}
                onChange={(event) => setEnabled(event.target.checked)}
              />
            }
            label={t("liveReactionsSettings.enabled")}
          />
          <Box>
            <Typography variant="subtitle2">
              {t("liveReactionsSettings.emojisTitle")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("liveReactionsSettings.emojisHint", {
                selected: emojis.length,
                max: MAX_REACTION_EMOJIS,
              })}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {ALL_REACTION_EMOJIS.map((emoji) => {
              const selected = emojis.includes(emoji);
              return (
                <Chip
                  key={emoji}
                  label={REACTION_EMOJI_GLYPHS[emoji]}
                  aria-pressed={selected}
                  color={selected ? "primary" : "default"}
                  variant={selected ? "filled" : "outlined"}
                  onClick={() =>
                    setEmojis((prev) => toggleReactionEmoji(prev, emoji))
                  }
                  sx={{ fontSize: 20, minWidth: 48 }}
                />
              );
            })}
          </Box>
          <Box>
            <Typography variant="subtitle2">
              {t("liveReactionsSettings.phrasesTitle")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("liveReactionsSettings.phrasesHint", {
                max: MAX_PHRASES,
                length: MAX_PHRASE_LENGTH,
              })}
            </Typography>
          </Box>
          {phrases.map((phrase, index) => {
            const error = errors[index] ?? null;
            return (
              <Stack
                key={index}
                direction="row"
                spacing={1}
                alignItems="flex-start"
              >
                <TextField
                  fullWidth
                  size="small"
                  value={phrase}
                  onChange={(event) =>
                    setPhrases((prev) =>
                      prev.map((value, i) =>
                        i === index ? event.target.value : value
                      )
                    )
                  }
                  error={error !== null}
                  helperText={error ? t(PHRASE_ERROR_KEYS[error]) : " "}
                />
                <Tooltip title={t("common.actions.delete")}>
                  <IconButton
                    onClick={() =>
                      setPhrases((prev) => prev.filter((_, i) => i !== index))
                    }
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
            );
          })}
          <Box>
            <Button
              onClick={() => setPhrases((prev) => [...prev, ""])}
              disabled={phrases.length >= MAX_PHRASES}
            >
              {t("liveReactionsSettings.addPhrase")}
            </Button>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={loading || hasErrors || !isDirty}
            >
              {t("common.actions.save")}
            </Button>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};
